/**
 * FuarAlaniSalonlariCard.jsx — bir fuar alanının salonları (backlog #26, 2026-09-15)
 *
 * Fuar Alanı Detay/Düzenle sayfasında ve Salonlar sayfasında AYNI bileşen: salon ekle, adını
 * değiştir, FİZİKSEL TAVAN ve İZİN VERİLEN YÜKSEKLİK (salonun varsayılan limiti) gir, salonu
 * fuar alanından kaldır. Servis: services/fuarAlaniSalonService.js.
 *
 * ⚠️ İzin verilen yükseklik o salondaki HER fuarın limitini kırpar; bir fuarın tek/çift katlı
 *    limitinin altına indirmek 409 döner ve engelleyen fuarlar listelenir (geçmiş fuarlar dahil).
 * ⚠️ Başka bir fuar alanıyla PAYLAŞILAN eski salon kaydının adı değiştirilemez (409) — rozetle
 *    işaretli; o durumda yeni salon eklenmeli.
 *
 * 2026-09-26:
 *   #194 Hizmetler dört durumlu: Bilinmiyor → Var → Yok → Harici Destek → Bilinmiyor (tıklama
 *        sırası). Brüt m² ve hizmetler artık FUAR ALANINDAKİ salona ait (V53) — WAS salon
 *        kaydının kendisine; paylaşılan bir kayıtta (ör. "1" altı fuar alanında) bir fuar alanında
 *        girilen değer ötekilerde de görünüyordu.
 *   #203 "Salon Planı" ve "Altyapı Planı" çizim durumu sütunları (Çizim yok / Çiziliyor /
 *        Tamamlandı) — rozete tıklayıp değiştirilir, Kaydet'i beklemeden hemen yazılır (#202).
 *   #193 Kalem düğmesi Salon Planı'nı kendi sayfasında tam düzenleme modunda açar
 *        (/Management/FairAreas/:fuarAlaniId/Salonlar/:salonId/Plan) — yalnızca personel.
 *        Harita düğmesinin salt okunur hızlı bakış modalı olduğu gibi kaldı (Bilge, 2026-09-26).
 *
 * 2026-10-08:
 *   #291 Hizmet rozetleri de plan durumları gibi menü açıyor (HizmetDurumuPill) — WAS: her
 *        tıklama sıradaki duruma geçiyordu. Plan durumlarından farkı: seçim taslağa yazılır,
 *        satırın Kaydet'i gönderir (Bilge, 2026-10-08).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// WAS: ..., ModalFooter, UncontrolledTooltip — tooltip yalnızca eski hizmet rozetlerindeydi; #291'de
// HizmetDurumuPill kendi tooltip'ini taşıyor.
import {
  Card, CardHeader, CardBody, Table, Input, InputGroup, InputGroupText, Button, Badge,
  Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { Map as MapIcon, Pencil } from "lucide-react";
import {
  listSalonlar, addSalon, updateSalon, removeSalon, listSalonHizmetTipleri,
  setSalonPlaniDurumu, setAltyapiPlaniDurumu,
} from "../../services/fuarAlaniSalonService";
import { isStaffAccount } from "../../services/firmaSessionService";
// WAS (#194): hizmetDurumMeta, nextHizmetDurum — yalnızca tıklama-döngülü hizmet rozetleri
// kullanıyordu; #291'de HizmetDurumuPill'e geçti.
// import {
//   hizmetDurumMeta, hizmetDurumOf, nextHizmetDurum, HIZMET_LEGEND, CizimDurumuPill,
// } from "./salonDurumlari";
import {
  hizmetDurumOf, HIZMET_LEGEND, CizimDurumuPill, HizmetDurumuPill,
} from "./salonDurumlari";
import { TEKNIK_ICONS } from "../Projects/StandPills";
import KrokiVectorEmbed from "../Kroki/KrokiVectorEmbed";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const fmtM = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(".", ",")} m`);
const fmtM2 = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(".", ",")} m²`);
const asInput = (v) => (v == null ? "" : String(v));
const EMPTY_NEW = { salonName: "", ceilingHeight: "", allowedHMax: "" };
// WAS (#100/#101): hizmetler[].mevcut → { true: var, false: yok, null: bilinmiyor }, and a touched
// badge could never go back to bilinmiyor (no delete endpoint).
// const hizmetlerToDraft = (hizmetler) => Object.fromEntries((hizmetler || []).map((x) => [x.kod, x.mevcut]));
// #194: durum 0 yok / 1 var / 2 harici destek / null bilinmiyor; PUT'ta null = bilinmiyor'a dön.
const hizmetlerToDraft = (hizmetler) => Object.fromEntries((hizmetler || []).map((x) => [x.kod, hizmetDurumOf(x)]));

/** İzin verilen > tavan ise ekranda kaydetmeden önce söyle; backend de 400 döner. */
function heightProblem(ceiling, allowed) {
  if (ceiling === "" || allowed === "") return null;
  return Number(allowed) > Number(ceiling) ? i18n.t("components:fuarAlaniSalonlariCard.izinVerilenYukseklikFizikselTavandan") : null;
}

export default function FuarAlaniSalonlariCard({ fuarAlaniId, title = "Salonlar", onChanged }) {
  const { t: tx } = useTranslation("components");
  const navigate = useNavigate();
  // #193: tam düzenleme yalnızca personel — firma oturumu bu kartı zaten görmez (FairAreas
  // rotaları [1,2,3]), ama kart başka yere gömülürse de düğme görünmesin.
  const personel = isStaffAccount();
  const [cizimBusy, setCizimBusy] = useState(null); // "salonId|plan" — o an yazılan rozet
  const [halls, setHalls] = useState([]);
  const [drafts, setDrafts] = useState({}); // salonId -> { salonName, ceilingHeight, allowedHMax, brutM2, hizmetler }
  const [newHall, setNewHall] = useState(EMPTY_NEW);
  const [hizmetTipleri, setHizmetTipleri] = useState([]); // [{ salonHizmetTipiId, kod, ad, sira }]
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  // Backlog #27 — salonun kroki (taban planı) görüntüleyicisi. Yalnızca görüntüleme:
  // KrokiVectorEmbed allowEdit={false} ile açılır, bu ekranın işi bir salonun planına
  // hızlı bakmak, düzenlemek değil.
  const [krokiTarget, setKrokiTarget] = useState(null);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 3500);
    return () => clearTimeout(t);
  }, [successMsg]);

  // Katalog fuar alanından bağımsız — bir kere çekilir. Boş/hatalı gelirse legend sessizce boş kalır.
  useEffect(() => {
    let ignore = false;
    listSalonHizmetTipleri().then((rows) => { if (!ignore) setHizmetTipleri(rows); }).catch(() => {});
    return () => { ignore = true; };
  }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!fuarAlaniId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      try {
        const rows = await listSalonlar(fuarAlaniId);
        if (ignore) return;
        setHalls(rows);
        const next = {};
        rows.forEach((h) => {
          next[h.salonId] = {
            salonName: h.salonName ?? "",
            ceilingHeight: asInput(h.ceilingHeight),
            allowedHMax: asInput(h.allowedHMax),
            brutM2: asInput(h.brutM2),
            hizmetler: hizmetlerToDraft(h.hizmetler),
          };
        });
        setDrafts(next);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarAlaniId, reloadToken]);

  async function runAction(fn, okMsg) {
    setBusy(true);
    setError(null);
    setConflicts(null);
    try {
      const out = await fn();
      setSuccessMsg(out?.message || okMsg);
      setReloadToken((n) => n + 1);
      if (onChanged) onChanged();
      return true;
    } catch (e) {
      setError(e.message);
      if (e.conflicts) setConflicts(e.conflicts);
      return false;
    } finally {
      setBusy(false);
    }
  }

  function changedFields(h) {
    const d = drafts[h.salonId];
    if (!d) return {};
    const patch = {};
    if (d.salonName.trim() !== (h.salonName ?? "")) patch.salonName = d.salonName;
    if (d.ceilingHeight !== asInput(h.ceilingHeight)) patch.ceilingHeight = d.ceilingHeight;
    if (d.allowedHMax !== asInput(h.allowedHMax)) patch.allowedHMax = d.allowedHMax;
    if (d.brutM2 !== asInput(h.brutM2)) patch.brutM2 = d.brutM2;
    const original = hizmetlerToDraft(h.hizmetler);
    const hizmetlerPatch = {};
    Object.entries(d.hizmetler || {}).forEach(([kod, mevcut]) => {
      if (mevcut !== (original[kod] ?? null)) hizmetlerPatch[kod] = mevcut;
    });
    if (Object.keys(hizmetlerPatch).length > 0) patch.hizmetler = hizmetlerPatch;
    return patch;
  }

  const setDraft = (salonId, key, value) =>
    setDrafts((p) => ({ ...p, [salonId]: { ...p[salonId], [key]: value } }));

  // WAS: bilinmiyor/yok → var, var → yok ({ [kod]: !current }).
  // #194: Bilinmiyor → Var → Yok → Harici Destek → Bilinmiyor.
  // WAS (#194, #291'de menüye geçti):
  // const toggleHizmet = (salonId, kod) =>
  //   setDrafts((p) => {
  //     const current = p[salonId]?.hizmetler?.[kod] ?? null;
  //     return { ...p, [salonId]: { ...p[salonId], hizmetler: { ...p[salonId]?.hizmetler, [kod]: nextHizmetDurum(current) } } };
  //   });
  // #291: menüden seçilen durum taslağa yazılır; satırın Kaydet'i gönderir (plan durumları gibi
  // hemen yazılmaz — Bilge, 2026-10-08).
  const setHizmet = (salonId, kod, durum) =>
    setDrafts((p) => ({
      ...p, [salonId]: { ...p[salonId], hizmetler: { ...p[salonId]?.hizmetler, [kod]: durum } },
    }));

  // #202/#203: çizim durumu hemen yazılır (taslak/Kaydet'ten bağımsız); dönen satır listede
  // yerine konur — taslaklara dokunulmaz, yarım kalmış bir düzenleme kaybolmasın.
  async function handleCizimDurumu(h, plan, durum) {
    const key = `${h.salonId}|${plan}`;
    setCizimBusy(key);
    setError(null);
    try {
      const fn = plan === "altyapiPlani" ? setAltyapiPlaniDurumu : setSalonPlaniDurumu;
      const out = await fn(fuarAlaniId, h.salonId, durum);
      if (out?.data) {
        setHalls((prev) => prev.map((x) => (x.salonId === h.salonId ? { ...x, ...out.data } : x)));
      } else {
        setReloadToken((n) => n + 1);
      }
      setSuccessMsg(out?.message || tx("fuarAlaniSalonlariCard.cizimDurumuKaydedildi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setCizimBusy(null);
    }
  }

  const handleSave = (h) =>
    runAction(() => updateSalon(fuarAlaniId, h.salonId, changedFields(h)), `${h.salonName} kaydedildi.`);

  const handleAdd = async () => {
    const ok = await runAction(() => addSalon(fuarAlaniId, newHall), `${newHall.salonName.trim()} eklendi.`);
    if (ok) setNewHall(EMPTY_NEW);
  };

  // Başarısızlıkta da modal kapanır — neden (ve engelleyen fuarlar) kartın üstündeki uyarıda.
  const handleRemove = async () => {
    const target = removeTarget;
    await runAction(() => removeSalon(fuarAlaniId, target.salonId), tx("fuarAlaniSalonlariCard.kaldirildi", { salonName: target.salonName }));
    setRemoveTarget(null);
  };

  const newProblem = heightProblem(newHall.ceilingHeight, newHall.allowedHMax);

  return (
    <Card>
      <CardHeader className="d-flex align-items-center gap-2">
        <h6 className="card-title mb-0 flex-grow-1">
          {title} <Badge color="light" className="text-dark ms-1">{halls.length}</Badge>
        </h6>
        <span className="text-muted small">{tx("fuarAlaniSalonlariCard.izinVerilenYukseklikOSalondaki")}</span>
        <Button color="light" size="sm" className="border" onClick={() => setReloadToken((n) => n + 1)} disabled={loading || busy} title={tx("common:yenile")}>
          <i className="bx bx-refresh" />
        </Button>
      </CardHeader>
      <CardBody className="p-0">
        {error && (
          <Alert color="danger" className="m-3" toggle={() => { setError(null); setConflicts(null); }}>
            {error}
            {conflicts?.length > 0 && (
              <ul className="mb-0 mt-2 small">
                {conflicts.map((c, i) => (
                  <li key={c.salonToFuarId ?? c.fuarId ?? i}>
                    {c.fuarName ?? (c.fuarId != null ? tx("fuarAlaniSalonlariCard.fuar", { fuarId: c.fuarId }) : c.salonName ?? tx("fuarAlaniSalonlariCard.kayit"))}
                    {(c.salonHMaxTekKat != null || c.salonHMaxCiftKat != null) &&
                      tx("fuarAlaniSalonlariCard.tekKatCiftKat", { fmtM: fmtM(c.salonHMaxTekKat), fmtM2: fmtM(c.salonHMaxCiftKat) })}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        {successMsg && <Alert color="success" className="m-3">{successMsg}</Alert>}

        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle table-nowrap mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3" style={{ minWidth: 180 }}>{tx("fuarAlaniSalonlariCard.salonAdi")}</th>
                  <th style={{ width: 170 }}>{tx("fuarAlaniSalonlariCard.fizikselTavan")}</th>
                  <th style={{ width: 170 }}>{tx("fuarAlaniSalonlariCard.izinVerilenYukseklik")}</th>
                  <th style={{ width: 130 }}>{tx("fuarAlaniSalonlariCard.brutM")}</th>
                  <th style={{ minWidth: 160 }}>
                    {tx("fuarAlaniSalonlariCard.hizmetler")}
                    {hizmetTipleri.length > 0 && (
                      <div className="fw-normal text-muted" style={{ fontSize: 11 }}>
                        {hizmetTipleri.map((t) => {
                          const Icon = TEKNIK_ICONS[t.kod.toLowerCase()];
                          return (
                            <span key={t.kod} className="me-2 d-inline-flex align-items-center gap-1">
                              {Icon && <Icon size={11} />}{t.ad}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="fw-normal d-flex flex-wrap gap-1 mt-1" style={{ fontSize: 10 }}>
                      {HIZMET_LEGEND.map((l) => (
                        <Badge key={l.label} color={l.color} pill className={l.className}>{l.label}</Badge>
                      ))}
                    </div>
                  </th>
                  <th className="text-center" style={{ width: 120 }} title={tx("fuarAlaniSalonlariCard.salonPlaniSalonDuvarlariCizim")}>
                    {tx("fuarAlaniSalonlariCard.salonPlani")}
                  </th>
                  <th className="text-center" style={{ width: 120 }} title={tx("fuarAlaniSalonlariCard.altyapiPlaniCizimDurumuTiklayip")}>
                    {tx("fuarAlaniSalonlariCard.altyapiPlani")}
                  </th>
                  <th className="text-center" style={{ width: 110 }}>{tx("fuarAlaniSalonlariCard.kullananFuar")}</th>
                  <th className="text-end pe-3" style={{ width: personel ? 150 : 110 }}>{tx("fuarAlaniSalonlariCard.islemler")}</th>
                </tr>
              </thead>
              <tbody>
                {halls.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-muted py-4">{tx("fuarAlaniSalonlariCard.buFuarAlanindaKayitliSalon")}</td></tr>
                )}
                {halls.map((h) => {
                  const d = drafts[h.salonId] ?? { salonName: "", ceilingHeight: "", allowedHMax: "", brutM2: "", hizmetler: {} };
                  const patch = changedFields(h);
                  const dirty = Object.keys(patch).length > 0;
                  const problem = heightProblem(d.ceilingHeight, d.allowedHMax);
                  return (
                    <tr key={h.salonId}>
                      <td className="ps-3">
                        <Input
                          bsSize="sm" value={d.salonName}
                          disabled={h.sharedWithOtherFairgrounds}
                          title={h.sharedWithOtherFairgrounds ? tx("fuarAlaniSalonlariCard.buSalonKaydiBaskaBir") : undefined}
                          onChange={(e) => setDraft(h.salonId, "salonName", e.target.value)}
                        />
                        {h.sharedWithOtherFairgrounds && (
                          <Badge color="light" className="text-muted border mt-1">{tx("fuarAlaniSalonlariCard.baskaFuarAlaniylaPaylasiliyor")}</Badge>
                        )}
                      </td>
                      <td>
                        <InputGroup size="sm">
                          <Input
                            type="number" min="0" step="0.1" placeholder={tx("fuarAlaniSalonlariCard.kayitYok")} value={d.ceilingHeight}
                            onChange={(e) => setDraft(h.salonId, "ceilingHeight", e.target.value)}
                          />
                          <InputGroupText>m</InputGroupText>
                        </InputGroup>
                      </td>
                      <td>
                        <InputGroup size="sm">
                          <Input
                            type="number" min="0" step="0.1" placeholder={tx("fuarAlaniSalonlariCard.kayitYok")} value={d.allowedHMax}
                            invalid={Boolean(problem)}
                            onChange={(e) => setDraft(h.salonId, "allowedHMax", e.target.value)}
                          />
                          <InputGroupText>m</InputGroupText>
                        </InputGroup>
                        {problem && <div className="text-danger small">{problem}</div>}
                      </td>
                      <td>
                        <InputGroup size="sm">
                          <Input
                            type="number" min="0" step="0.1" placeholder={tx("fuarAlaniSalonlariCard.kayitYok")} value={d.brutM2}
                            onChange={(e) => setDraft(h.salonId, "brutM2", e.target.value)}
                          />
                          <InputGroupText>m²</InputGroupText>
                        </InputGroup>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {/* WAS (#194, tıklama döngüsü — #291'de menüye geçti):
                          {hizmetTipleri.map((t) => {
                            // WAS: mevcut true/false/null → success/secondary/light (#100/#101).
                            const durum = d.hizmetler?.[t.kod] ?? null;
                            const meta = hizmetDurumMeta(durum);
                            const Icon = TEKNIK_ICONS[t.kod.toLowerCase()];
                            const badgeId = `salon-hizmet-${h.salonId}-${t.kod}`;
                            return (
                              <React.Fragment key={t.kod}>
                                <Badge
                                  id={badgeId} color={meta.color} pill
                                  className={`d-inline-flex align-items-center gap-1 ${meta.className}`}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => toggleHizmet(h.salonId, t.kod)}
                                >
                                  {Icon && <Icon size={11} />}
                                </Badge>
                                <UncontrolledTooltip target={badgeId} placement="top">
                                  {t.ad}: {meta.label} — tıklayın: Bilinmiyor → Var → Yok → Harici Destek
                                </UncontrolledTooltip>
                              </React.Fragment>
                            );
                          })}
                          */}
                          {/* #291: tıklayınca dört seçenekli menü; seçim taslağa yazılır, satırın Kaydet'i gönderir. */}
                          {hizmetTipleri.map((t) => (
                            <HizmetDurumuPill
                              key={t.kod}
                              id={`salon-hizmet-${h.salonId}-${t.kod}`}
                              ad={t.ad}
                              Icon={TEKNIK_ICONS[t.kod.toLowerCase()]}
                              durum={d.hizmetler?.[t.kod] ?? null}
                              onChange={(durum) => setHizmet(h.salonId, t.kod, durum)}
                            />
                          ))}
                        </div>
                      </td>
                      <td className="text-center">
                        <CizimDurumuPill
                          id={`salon-plani-${h.salonId}`} plan="salonPlani"
                          durum={h.salonPlaniDurum ?? 0} tarih={h.salonPlaniDurumTarihi}
                          busy={cizimBusy === `${h.salonId}|salonPlani`}
                          disabled={!personel || Boolean(cizimBusy)}
                          onChange={(durum) => handleCizimDurumu(h, "salonPlani", durum)}
                        />
                      </td>
                      <td className="text-center">
                        <CizimDurumuPill
                          id={`altyapi-plani-${h.salonId}`} plan="altyapiPlani"
                          durum={h.altyapiPlaniDurum ?? 0} tarih={h.altyapiPlaniDurumTarihi}
                          busy={cizimBusy === `${h.salonId}|altyapiPlani`}
                          disabled={!personel || Boolean(cizimBusy)}
                          onChange={(durum) => handleCizimDurumu(h, "altyapiPlani", durum)}
                        />
                      </td>
                      <td className="text-center">
                        <Badge color={h.fuarCount > 0 ? "info" : "light"} className={h.fuarCount > 0 ? "" : "text-muted"} pill>
                          {h.fuarCount ?? 0}
                        </Badge>
                      </td>
                      <td className="text-end pe-3">
                        <div className="d-flex gap-1 justify-content-end">
                          <Button
                            size="sm" color="soft-info" className="border" title={tx("fuarAlaniSalonlariCard.salonTabanPlaniKrokiHizli")}
                            onClick={() => setKrokiTarget(h)}
                          >
                            <MapIcon size={14} />
                          </Button>
                          {personel && (
                            // #228: katmansız açılır → sayfa önce katmanı sordurur (Salon Planı / Altyapı Planı).
                            // WAS: title="Salon Planı'nı düzenle (tam ekran)" — sayfa sessizce Salon Planı'nda açılıyordu.
                            <Button
                              size="sm" color="soft-warning" className="border" title={tx("fuarAlaniSalonlariCard.salonAltyapiPlaniNiDuzenle")}
                              onClick={() => navigate(`/Management/FairAreas/${fuarAlaniId}/Salonlar/${h.salonId}/Plan`)}
                            >
                              <Pencil size={14} />
                            </Button>
                          )}
                          <Button
                            size="sm" color="light" className="border" title={tx("common:kaydet")}
                            disabled={busy || !dirty || Boolean(problem) || !d.salonName.trim()}
                            onClick={() => handleSave(h)}
                          >
                            <i className="bx bx-save" />
                          </Button>
                          <Button
                            size="sm" color="danger" outline title={h.fuarCount > 0 ? tx("fuarAlaniSalonlariCard.buFuarAlanindaOnuKullanan") : tx("fuarAlaniSalonlariCard.fuarAlanindanKaldir")}
                            disabled={busy}
                            onClick={() => setRemoveTarget(h)}
                          >
                            <i className="bx bx-trash" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* ── Yeni salon satırı ── */}
                <tr className="table-light">
                  <td className="ps-3">
                    <Input
                      bsSize="sm" placeholder={tx("fuarAlaniSalonlariCard.yeniSalonAdi")} value={newHall.salonName}
                      onChange={(e) => setNewHall((p) => ({ ...p, salonName: e.target.value }))}
                    />
                  </td>
                  <td>
                    <InputGroup size="sm">
                      <Input
                        type="number" min="0" step="0.1" placeholder={tx("fuarAlaniSalonlariCard.tavan")} value={newHall.ceilingHeight}
                        onChange={(e) => setNewHall((p) => ({ ...p, ceilingHeight: e.target.value }))}
                      />
                      <InputGroupText>m</InputGroupText>
                    </InputGroup>
                  </td>
                  <td>
                    <InputGroup size="sm">
                      <Input
                        type="number" min="0" step="0.1" placeholder={tx("fuarAlaniSalonlariCard.izinVerilen")} value={newHall.allowedHMax}
                        invalid={Boolean(newProblem)}
                        onChange={(e) => setNewHall((p) => ({ ...p, allowedHMax: e.target.value }))}
                      />
                      <InputGroupText>m</InputGroupText>
                    </InputGroup>
                    {newProblem && <div className="text-danger small">{newProblem}</div>}
                  </td>
                  <td className="text-muted small">{tx("fuarAlaniSalonlariCard.eklediktenSonra")}</td>
                  <td className="text-muted small">{tx("fuarAlaniSalonlariCard.eklediktenSonra")}</td>
                  <td />
                  <td />
                  <td />
                  <td className="text-end pe-3">
                    <Button
                      size="sm" color="primary"
                      disabled={busy || !newHall.salonName.trim() || Boolean(newProblem)}
                      onClick={handleAdd}
                    >
                      {busy ? <Spinner size="sm" /> : <><i className="bx bx-plus me-1" />{tx("common:ekle")}</>}
                    </Button>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      <Modal isOpen={Boolean(removeTarget)} toggle={() => !busy && setRemoveTarget(null)} centered>
        <ModalHeader toggle={() => !busy && setRemoveTarget(null)}>{tx("fuarAlaniSalonlariCard.salonuKaldir")}</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            <span className="fw-semibold">{removeTarget?.salonName}</span>{" "}{tx("fuarAlaniSalonlariCard.buFuarAlanininSalonListesinden")}
          </p>
          <p className="text-muted small mb-0">
            {tx("fuarAlaniSalonlariCard.buFuarAlanindaOnuKullanan2")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={busy} onClick={() => setRemoveTarget(null)}>{tx("fuarAlaniSalonlariCard.vazgec")}</Button>
          <Button color="danger" disabled={busy} onClick={handleRemove}>
            {busy ? <Spinner size="sm" /> : tx("fuarAlaniSalonlariCard.kaldir")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Backlog #27 — salon taban planı görüntüleyici. allowEdit={false}: bu ekranın
          işi bir salona hızlı bakmak, kroki uygulamasında düzenleme oturumu açmak
          değil. Yalnızca infrastructure/floorplan katmanları gösterilir
          (krokiVectorService.buildEmbedUrl, backlog #28's layer parametresi). */}
      <Modal isOpen={Boolean(krokiTarget)} toggle={() => setKrokiTarget(null)} centered size="lg">
        <ModalHeader toggle={() => setKrokiTarget(null)}>
          {krokiTarget?.salonName ?? tx("fuarAlaniSalonlariCard.salon")}{" "}{tx("fuarAlaniSalonlariCard.tabanPlani")}
        </ModalHeader>
        <ModalBody>
          {krokiTarget && (
            <KrokiVectorEmbed
              fairgroundId={fuarAlaniId}
              hallId={krokiTarget.salonId}
              hallName={krokiTarget.salonName}
              allowEdit={false}
              height={520}
            />
          )}
        </ModalBody>
      </Modal>
    </Card>
  );
}
